import { Box, } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { Dialog } from "../../../../framework/Dialog/Dialog";
import { DialogButtons } from "../../../../framework/Dialog/DialogButtons";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { Base } from "ui/Scripts/source/framework/base";
import { DialogSettingsCard } from "source/components/framework/Container/DialogSettingsCard";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { ListIconButtonsItem } from "source/components/framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { List } from "source/components/framework/List/List";
import { ChartDesignerValueFieldDialog, getValueFieldCalculationTypeOptions, taskChartValueFieldCalculationTypeToString, ValueFieldDialogItem } from "./ChartValueFieldDialog";
import { ListLinkValue } from "source/components/framework/List/ListLinkValue";
import { ArrowUpToolButton } from "source/components/framework/Button/ArrowUpToolButton";
import { ArrowDownToolButton } from "source/components/framework/Button/ArrowDownToolButton";
import { AppUtils } from "source/framework/appUtils";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskTypeChart } from "ui/Scripts/source/models/task/taskTypeChart";
import { TaskChartValueFieldCalculationType } from "ui/Scripts/source/models/enums";

interface IChartValueFieldsDialogProps {
    taskType: ITaskEditTypeItem;
    selectedTaskTypeChart: ITaskTypeChart;
    onSave: (items: ValueFieldDialogItem[]) => void;
    onClose: () => void;
}

export const ChartValueFieldsDialog = (
    props: IChartValueFieldsDialogProps
) => {
    const [availableFields, setAvailableFields] = useState<IIdTitle[]>([]);
    const [valueFieldItems, setValueFieldItems] = useState<ValueFieldDialogItem[]>([]);
    const [selectionData, setSelectionData] = useState<{ field: string, index: number }>({ field: "", index: -1 });
    const [valueFieldDialogData, setValueFieldDialogData] = useState<{ item: ValueFieldDialogItem, index: number, isNew: boolean, availableFields: IIdTitle[] }>({ item: null, index: 0, isNew: false, availableFields: [] });

    useEffect(() => {
        setAvailableFields(props.taskType.formCols.map(c => IdTitle.createIdTitle(c.field, c.title || c.field)));
        setValueFieldItems(props.selectedTaskTypeChart.valueFields.map(i => {
            return {
                field: i.field,
                title: i.title,
                calculationType: i.calculationType ?? 0,
                showDataLabels: i.showDataLabels ?? false
            };
        }));
    }, [props.taskType?.formCols, props.selectedTaskTypeChart?.valueFields]);

    const handleSetSelectedIds = (field: string, _checkedIds: string[]) => {
        const index = valueFieldItems.findIndex(i => i.field === field);
        setSelectionData({
            field,
            index
        });
    };
    
    const handleOpen = (field: string) => {
        const index = valueFieldItems.findIndex(i => i.field === field);
        if (index < 0) return;
        const usedFields = Base.getUniqueStringItems(valueFieldItems.map(i => i.field).concat(props.selectedTaskTypeChart.groupingFields.map(i => i.field)));
        const item = valueFieldItems[index];
        if (!item.title) {
            item.title = props.taskType.formCols.find(col => col.field === item.field)?.title ?? item.field;
        }
        setValueFieldDialogData({
            item,
            index,
            isNew: false,
            availableFields: availableFields.filter(i => !usedFields.find(j => j.equalIgnoreCase(i.id)) || i.id.equalIgnoreCase(item.field))
        });
    };

    const handleValueFieldDialogClose = () => {
        setValueFieldDialogData({
            item: null,
            index: 0,
            isNew: false,
            availableFields: []
        });
    };

    const handleValueFieldDialogSaved = (item: ValueFieldDialogItem) => {
        if (!item) return;
        if (valueFieldDialogData.isNew) {
            setValueFieldItems((old) => [...old, item]);
        } else {
            setValueFieldItems((old) => {
                old[valueFieldDialogData.index] = item;
                return [...old];
            });
        }
        setValueFieldDialogData((old) => ({
            ...old,
            isNew: false
        }));
    };

    const handleRemove = (field: string) => {
        if (!field) return;
        handleValueFieldDialogClose();
        setValueFieldItems((old) => [...old.filter((item) => item.field !== field)]);
        setSelectionData({
            field: "",
            index: -1
        });
    };

    const handleAdd = () => {
        const usedFields = Base.getUniqueStringItems(valueFieldItems.map(i => i.field).concat(props.selectedTaskTypeChart.groupingFields.map(i => i.field)));
        const dialogAvailableFields = availableFields.filter(i => !usedFields.find(j => j.equalIgnoreCase(i.id)));
        if (dialogAvailableFields.length < 1) {
            AppUtils.showErrorMessage(Translations.NoUnusedFormFieldsAvailable);
            return;
        }
        const field = dialogAvailableFields[0].id;
        const valueFieldCalculationTypeOptions = getValueFieldCalculationTypeOptions(props.taskType, field);
        const calculationType = parseInt(valueFieldCalculationTypeOptions[0].id, 10);
        setValueFieldDialogData({
            item: {
                field,
                calculationType,
                title: calculationType === TaskChartValueFieldCalculationType.Count
                    ? Translations.TaskChartValueFieldCalculationTypeCount
                    : dialogAvailableFields[0].title,
                showDataLabels: false
            },
            index: -1,
            isNew: true,
            availableFields: dialogAvailableFields
        });
    };

    const handleSave = () => {
        if (valueFieldItems.length < 1) {
            AppUtils.showErrorMessage(Translations.ChartAtLeastOneValueFieldMustBeDefined);
            return;
        }
        props.onSave(valueFieldItems);
    };

    const setEditDialogItemByIndex = (index: number) => {
        const usedFields = Base.getUniqueStringItems(valueFieldItems.map(i => i.field).concat(props.selectedTaskTypeChart.groupingFields.map(i => i.field)));
        const item = valueFieldItems[index];
        setValueFieldDialogData({
            item,
            index,
            isNew: false,
            availableFields: availableFields.filter(i => !usedFields.find(j => j.equalIgnoreCase(i.id)) || i.id.equalIgnoreCase(item.field))
        });
        setSelectionData({
            field: item.title,
            index
        });
    };

    const handleMoveToPrevious = () => {
        if (!valueFieldDialogData.item || valueFieldDialogData.index < 1 || valueFieldDialogData.index > valueFieldItems.length) return;
        setEditDialogItemByIndex(valueFieldDialogData.index - 1);
    };

    const handleMoveToNext = () => {
        console.log("handleMoveToNext", valueFieldDialogData);
        if (!valueFieldDialogData.item || valueFieldDialogData.index < 0 || valueFieldDialogData.index >= valueFieldItems.length - 1) return;
        setEditDialogItemByIndex(valueFieldDialogData.index + 1);
    };
    
    const swapValueFieldDialogItems = (oldIndex: number, newIndex: number) => {
        const newIdOptions = Base.swapArrayItems(valueFieldItems.slice(0), oldIndex, newIndex);
        setValueFieldItems(newIdOptions);
        setSelectionData({
            field: newIdOptions[newIndex].field,
            index: newIndex
        });
    };

    const handleMoveItemUp = () => {
        if (selectionData.index < 1) return;
        swapValueFieldDialogItems(selectionData.index, selectionData.index - 1);
    };
    
    const handleMoveItemDown = () => {
        if (selectionData.index < 0 || selectionData.index >= valueFieldItems.length - 1) return;
        swapValueFieldDialogItems(selectionData.index, selectionData.index + 1);
    };

    return (
        <Dialog
            id="chartDesignerChartPropertiesValueFieldsDialog"
            maxWidth="sm"
            maxHeight="600px"
            title={Translations.ChartValueFields}
            body={
                <>
                    <DialogSettingsCard
                        fitToContainerHeight
                        showCard={false}
                        cardContent={
                            <ActionContainer
                                margin="0"
                                rightItems={
                                    <ListIconButtonsItem
                                        prefixElement={
                                            <div>
                                                <ArrowUpToolButton onClick={handleMoveItemUp} disabled={selectionData.index < 1} />
                                                <ArrowDownToolButton onClick={handleMoveItemDown} disabled={selectionData.index < 0 || selectionData.index >= valueFieldItems.length - 1} />
                                            </div>
                                        }
                                        removeDisabled={!selectionData.field}
                                        onAdd={handleAdd}
                                        onRemove={() => handleRemove(selectionData.field)}
                                    />
                                }
                            />
                        }
                        content={
                            <List
                                intialized={true}
                                isLoading={false}
                                hasMore={false}
                                globalLoadingInUse={true}
                                height="100%"
                                header={{
                                    columns: [
                                        { size: 4, column: "field", content: Translations.FormField },
                                        { size: 4, column: "value", content: Translations.Value },
                                        { size: 4, column: "title", content: Translations.Title }
                                    ]
                                }}
                                lines={valueFieldItems.map((item) => {
                                    const defaultTitle = props.taskType.formCols.find(col => col.field === item.field)?.title ?? item.field;
                                    return {
                                        columns: [
                                            { size: 4, content: <ListLinkValue onClick={() => handleOpen(item.field)}>{defaultTitle}</ListLinkValue> },
                                            { size: 4, content: taskChartValueFieldCalculationTypeToString(item.calculationType) },
                                            { size: 4, content: item.title || defaultTitle }
                                        ],
                                        id: item.field
                                    };
                                })}
                                sortColumn={""}
                                sortOrderIsAsc={true}
                                selectedId={selectionData.field}
                                checkedIds={[]}
                                onSetSelectedIds={handleSetSelectedIds}
                                onSearchLines={() => {}}
                                onOpenLine={handleOpen}
                            />
                        } />
                    {!!valueFieldDialogData.item &&
                        <ChartDesignerValueFieldDialog
                            taskType={props.taskType}
                            availableFields={valueFieldDialogData.availableFields}
                            item={valueFieldDialogData.item}
                            isNew={valueFieldDialogData.isNew}
                            hasPrevious={valueFieldDialogData.index > 0}
                            hasNext={valueFieldDialogData.index < valueFieldItems.length - 1}
                            onSaved={handleValueFieldDialogSaved}
                            onClose={handleValueFieldDialogClose}
                            onRemove={handleRemove}
                            onPrevious={handleMoveToPrevious}
                            onNext={handleMoveToNext}
                        />
                    }
                </>
            }
            actions={
                <Box display="flex" justifyContent="flex-end" width="100%">
                    <DialogButtons
                        saveTitle={Translations.OK}
                        onCancel={props.onClose}
                        onSave={handleSave}
                    />
                </Box>
            }
            onClose={props.onClose}
        />
    );
};
